import React from 'react';
import { useTranslation } from 'react-i18next';
import { WandSparkles } from 'lucide-react';
import { Button, ButtonProps } from 'src/components/ui/button/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { cn } from 'src/lib/utils';

interface HelpMeWriteButtonProps extends Omit<ButtonProps, 'children' | 'onClick'> {
  onOpen: () => void;
  /**
   * Hide the text label on mobile (icon-only), restoring it at `md+`. Opt-in
   * for tight single rows like the Revisions chat input where the full label
   * would push the Send button off the row (#2484). Off by default so the
   * label stays for discoverability where there's room (#2290).
   */
  hideLabelOnMobile?: boolean;
  /**
   * Drop the text label entirely and render a compact square icon-only button
   * (#2499) — used on the Create Request / outcome Description rows where it
   * sits beside the field label. The label moves to an accessible name
   * (aria-label + native title tooltip) so it stays discoverable.
   */
  iconOnly?: boolean;
}

/**
 * Trigger for the Help-me-write panel.
 *
 * TEMPORARILY DISABLED: the AI-assist "Help me write" feature is not yet ready
 * for customers, so the trigger no longer opens the panel. It renders as a
 * greyed-out, non-interactive icon button (real `disabled`, so the cva
 * `disabled:` styles swap the fill to the muted `inactive` token) that surfaces
 * a "Coming soon" tooltip on hover/focus. A disabled button swallows its own
 * pointer events, so the tooltip hangs off a hoverable wrapper span rather than
 * the button.
 *
 * Re-enabling is a one-file revert: drop the Tooltip wrapper, restore
 * `onClick={onOpen}` on the (non-disabled) button, and bring back the brand
 * gradient + `iconOnly` / `hideLabelOnMobile` label rendering. The
 * HelpMeWritePanel and all three call sites are deliberately left untouched.
 */
const HelpMeWriteButton: React.FC<HelpMeWriteButtonProps> = ({
  className,
  variant = 'unset',
  size = 'sm',
  // Disabled-state trigger ignores the panel props; kept so the three call
  // sites compile unchanged and re-enabling stays a one-file revert.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onOpen, hideLabelOnMobile = false, iconOnly = false,
  ...rest
}) => {
  const { t } = useTranslation();
  const label = t('ai_assist.trigger', { defaultValue: 'Help me write' });
  const comingSoon = t('ai_assist.coming_soon', { defaultValue: 'Coming soon' });
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* The disabled button has `pointer-events: none`, so hover/focus is
              caught here on the wrapper to keep the tooltip reachable. */}
          <span
            tabIndex={0}
            className={cn(
              'tw-inline-flex tw-cursor-not-allowed tw-rounded-lg focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2',
              className,
            )}
          >
            <Button
              type={'button'}
              variant={variant}
              size={size}
              disabled
              aria-label={label}
              className={'tw-aspect-square tw-w-9 tw-shrink-0 tw-p-0'}
              {...rest}
            >
              <WandSparkles className={'tw-size-4'} />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{comingSoon}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export { HelpMeWriteButton };
